import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { format, addDays, startOfDay, isBefore, isSameDay, parse, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar, Plus, Minus, Users, ChevronLeft, ChevronRight, Clock, MapPin, LocateFixed } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn, formatAddressForDisplay } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay, defaultDropAnimationSideEffects } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { useScrollLock } from '@/hooks/useScrollLock';
import LocationAutocomplete from '../LocationAutocomplete';
import { AddressPickerSidebar } from '../AddressPickerSidebar';
import { getUserAddresses, type UserAddress } from '@/lib/supabase/userAddresses';
import { useSession } from '@/context/SessionContext';
import { useToast } from '@/components/ui/use-toast';

export interface Passenger {
  id: string;
  name: string;
  documentNumber: string; // This corresponds to documentNumber in the hook
  nationality?: string;
}

export interface AddressOption {
  id: string;
  displayName: string;
  coords: { lat: number; lng: number };
  floor?: string | null;
  apartment?: string | null;
  salutation?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

interface SelectedTourDay {
  date: string;
  tour: any;
  tourId?: string; // Explicit tour ID for unambiguous tracking
  destination?: {
    displayName: string;
    coords: { lat: number; lng: number };
  };
}

interface PassengerDetailsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  quantity: number;
  selectedTours: any[]; // Changed to handle multiple
  onProceedToCheckout: (passengers: Passenger[], tourDays: SelectedTourDay[], totalPrice: number, pickup?: AddressOption) => void;
  loading?: boolean;
  proceedLabel?: string;
  closeLabel?: string;
  initialPassengers?: Passenger[];
  initialTourDays?: SelectedTourDay[];
  initialPickup?: AddressOption;
}

const resolveTourUnitPrice = (tour: any): number => {
  const raw =
    tour?.fixed_sale_price ??
    tour?.sale_price ??
    tour?.price ??
    tour?.unit_price ??
    0;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Sortable Item Component
const SortableTourItem = ({ tourDay, onUpdateTour }: { 
  tourDay: SelectedTourDay, 
  onUpdateTour: (date: string, tour: any) => void 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: tourDay.date });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.3 : 1,
    touchAction: 'none', // Prevent scrolling/text selection on mobile during drag
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      {...attributes}
      {...listeners}
      className={cn(
        "bg-background rounded-lg border p-3 space-y-3 shadow-sm transition-all relative overflow-hidden cursor-grab active:cursor-grabbing hover:border-primary/50 group/item select-none",
        isDragging && "ring-2 ring-primary ring-offset-2 scale-[1.02] shadow-xl z-50 cursor-grabbing bg-primary/5"
      )}
    >
      <div className="flex items-center justify-between border-b pb-2">
        <div className="flex items-center gap-2">
          {/* Visual Indicator instead of Drag Handle */}
          <div className="p-1 text-muted-foreground/30 group-hover/item:text-primary transition-colors">
            <div className="grid grid-cols-2 gap-0.5 w-3.5">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="w-1 h-1 bg-current rounded-full" />
              ))}
            </div>
          </div>
          <span className="text-xs font-bold text-primary uppercase">
            {format(parse(tourDay.date, 'yyyy-MM-dd', new Date()), "EEEE d 'de' MMMM", { locale: es })}
          </span>
        </div>
      </div>
      
      <div className="flex items-start justify-between gap-2 px-1 pointer-events-none select-none">
        <div className="text-sm font-bold text-foreground flex items-start gap-2 flex-1 min-w-0">
          <div className="w-2 h-2 rounded-full bg-primary shadow-[0_0_8px_rgba(var(--primary),0.5)] flex-shrink-0 mt-1" />
          {tourDay.tour?.title ? (
            <span className="break-words">{tourDay.tour.title}</span>
          ) : (
            <span className="break-words italic text-muted-foreground">Seleccionar excursión...</span>
          )}
        </div>
        {tourDay.tour?.departure_time && (
          <div className="text-[10px] bg-primary/10 px-2 py-0.5 rounded-full text-primary font-bold flex items-center gap-1 whitespace-nowrap flex-shrink-0">
            <Clock className="w-2.5 h-2.5" />
            {tourDay.tour.departure_time.substring(0, 5)} hs
          </div>
        )}
      </div>
    </div>
  );
};

export const PassengerDetailsSidebar: React.FC<PassengerDetailsSidebarProps> = ({
  isOpen,
  onClose,
  quantity,
  selectedTours,
  onProceedToCheckout,
  loading = false,
  proceedLabel = 'Confirmar',
  closeLabel = 'Volver',
  initialPassengers,
  initialTourDays,
  initialPickup,
}) => {
  const { session } = useSession();
  const { toast } = useToast();
  const addressPickerRef = useRef<HTMLDivElement>(null);

  // Pickup state
  const [selectedPickup, setSelectedPickup] = useState<AddressOption | null>(initialPickup || null);
  const [addresses, setAddresses] = useState<UserAddress[]>([]);
  const [addressPickerOpen, setAddressPickerOpen] = useState<'pickup' | null>(null);
  const [initialLocationForPicker, setInitialLocationForPicker] = useState<{ lat: number; lng: number } | null>(null);
  const [initialDisplayNameForPicker, setInitialDisplayNameForPicker] = useState<string | null>(null);
  
  // Initialize calendar to today's date in local timezone
  const getTodayDate = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  // Load user addresses when sidebar opens
  useEffect(() => {
    if (!isOpen) return;
    if (session?.user?.id) {
      getUserAddresses(session.user.id)
        .then(setAddresses)
        .catch(() => setAddresses([]));
    } else {
      try {
        const local = localStorage.getItem('pending_addresses');
        if (local) {
          const parsed = JSON.parse(local);
          setAddresses(parsed.map((addr: any) => ({
            id: addr.id,
            user_id: null,
            label: addr.label,
            is_primary: addr.is_primary || false,
            location: addr.location,
            floor: addr.floor,
            apartment: addr.apartment,
            notes: addr.notes,
            altura: addr.altura,
            streetName: addr.streetName,
            created_at: addr.created_at,
            updated_at: addr.updated_at,
          })));
        }
      } catch {
        setAddresses([]);
      }
    }
  }, [isOpen, session?.user?.id]);

  // Reset pickup when initialPickup changes
  useEffect(() => {
    if (initialPickup) setSelectedPickup(initialPickup);
  }, [initialPickup]);

  const handleAddressPickerClose = useCallback(() => {
    setAddressPickerOpen(null);
    setInitialLocationForPicker(null);
  }, []);

  const handleSelectAddress = useCallback((address: AddressOption) => {
    setSelectedPickup(address);
    setAddressPickerOpen(null);
    setInitialLocationForPicker(null);
  }, []);

  const handleUseCurrentLocationForPickup = useCallback(() => {
    if (!navigator.geolocation) {
      toast({ title: 'GPS no disponible', description: 'Tu navegador no soporta geolocalización.' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setInitialLocationForPicker({ lat: position.coords.latitude, lng: position.coords.longitude });
        setInitialDisplayNameForPicker(null);
        setAddressPickerOpen('pickup');
      },
      () => {
        toast({ title: 'Error GPS', description: 'No fue posible obtener la ubicación.' });
      }
    );
  }, [toast]);

  const [passengers, setPassengers] = useState<Passenger[]>([]);
  const [totalPassengers, setTotalPassengers] = useState(quantity);
  const [draftReady, setDraftReady] = useState(false);

  const buildDefaultPassengers = useCallback((count: number) => {
    return Array.from({ length: count }).map(() => ({
      id: crypto.randomUUID(),
      name: '',
      documentNumber: '',
      nationality: '',
    }));
  }, []);

  const localDraftKey = useMemo(() => {
    const ids = selectedTours
      .map((tour: any) => String(tour?.id || ''))
      .filter(Boolean)
      .sort()
      .join('-');
    return `tour-passenger-draft:${ids || 'generic'}`;
  }, [selectedTours]);

  const hydrateTourDays = useCallback((days: SelectedTourDay[]) => {
    if (!Array.isArray(days) || days.length === 0) return [];

    return days.map((day) => {
      // Try to find tour by explicit tourId first, then by tour.id, then by title
      const tourById = selectedTours.find((t: any) => String(t?.id) === String(day?.tourId || day?.tour?.id));
      const tourByTitle = !tourById
        ? selectedTours.find((t: any) => String(t?.title || '').trim() === String(day?.tour?.title || '').trim())
        : null;
      const matchedTour = tourById || tourByTitle;
      if (!matchedTour) return day;

      return {
        ...day,
        tourId: String(matchedTour.id), // Explicitly set tourId
        tour: {
          ...day.tour,
          ...matchedTour,
        },
      };
    });
  }, [selectedTours]);

  // Restore draft when sidebar opens
  React.useEffect(() => {
    if (!isOpen) return;

    setDraftReady(false);

    try {
      const raw = localStorage.getItem(localDraftKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        const parsedPassengers = Array.isArray(parsed?.passengers) ? parsed.passengers : [];
        const parsedTourDays = Array.isArray(parsed?.selectedTourDays) ? parsed.selectedTourDays : [];

        if (parsedPassengers.length > 0) {
          setPassengers(parsedPassengers);
          setTotalPassengers(Math.max(1, Number(parsed?.totalPassengers || parsedPassengers.length || quantity)));
        } else if (initialPassengers && initialPassengers.length > 0) {
          setPassengers(initialPassengers);
          setTotalPassengers(Math.max(1, initialPassengers.length));
        } else {
          setPassengers(buildDefaultPassengers(quantity));
          setTotalPassengers(quantity);
        }

        const restoredTourDays = parsedTourDays.length > 0 ? parsedTourDays : (initialTourDays || []);
        setSelectedTourDays(hydrateTourDays(restoredTourDays));

        if (parsed?.currentCalendarMonth) {
          const restoredMonth = new Date(parsed.currentCalendarMonth);
          if (!Number.isNaN(restoredMonth.getTime())) {
            setCurrentCalendarMonth(restoredMonth);
          }
        }
      } else if (initialPassengers && initialPassengers.length > 0) {
        // Fallback: use data from cart notes passed as props
        setPassengers(initialPassengers);
        setTotalPassengers(Math.max(1, initialPassengers.length));
        setSelectedTourDays(hydrateTourDays(initialTourDays || []));
      } else {
        setPassengers(buildDefaultPassengers(quantity));
        setTotalPassengers(quantity);
        setSelectedTourDays([]);
      }
    } catch {
      setPassengers(buildDefaultPassengers(quantity));
      setTotalPassengers(quantity);
      setSelectedTourDays([]);
    } finally {
      setDraftReady(true);
    }
  }, [isOpen, localDraftKey, quantity, buildDefaultPassengers, initialPassengers, initialTourDays, hydrateTourDays]);

  const [selectedTourDays, setSelectedTourDays] = useState<SelectedTourDay[]>([]);
  const selectedDayQueueRef = useRef<string[]>([]);
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [missingSection, setMissingSection] = useState<'pickup' | 'days' | 'passengers' | null>(null);
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const pickupSectionRef = useRef<HTMLDivElement>(null);
  const daysSectionRef = useRef<HTMLDivElement>(null);
  const passengersSectionRef = useRef<HTMLDivElement>(null);

  // Keep a stable FIFO order of selected dates so over-capacity picks replace a previously selected day.
  useEffect(() => {
    const currentDates = selectedTourDays.map((d) => d.date);
    selectedDayQueueRef.current = selectedDayQueueRef.current.filter((d) => currentDates.includes(d));
    currentDates.forEach((d) => {
      if (!selectedDayQueueRef.current.includes(d)) {
        selectedDayQueueRef.current.push(d);
      }
    });
  }, [selectedTourDays]);

  /**
   * Reassign tours when selectedTours changes (add/remove tours from cart)
   * Maintains 1:1 mapping between tours and dates
   */
  useEffect(() => {
    if (!isOpen || selectedTourDays.length === 0 || selectedTours.length === 0) return;
    
    setSelectedTourDays((prev) => {
      const validatedDays = prev.map((day) => {
        // Verify tour still exists in selectedTours
        const tourId = String(day.tourId || day.tour?.id || '').trim();
        const tourExists = selectedTours.some(
          (t: any) => String(t?.id || '').trim() === tourId
        );
        
        if (!tourExists && selectedTours.length > 0) {
          // Tour was removed: reassign to first available tour
          const firstAvailableTour = selectedTours[0];
          return {
            ...day,
            tourId: String(firstAvailableTour?.id || ''),
            tour: { ...firstAvailableTour },
            destination: {
              displayName: firstAvailableTour?.title || 'Sin nombre',
              coords: { lat: firstAvailableTour?.latitude || 0, lng: firstAvailableTour?.longitude || 0 },
            },
          };
        }
        
        // Tour exists: ensure we have complete tour data
        if (tourId) {
          const matchedTour = selectedTours.find(
            (t: any) => String(t?.id || '').trim() === tourId
          );
          if (matchedTour) {
            return {
              ...day,
              tourId: String(matchedTour.id),
              tour: { ...matchedTour },
              destination: day.destination || {
                displayName: matchedTour?.title || 'Sin nombre',
                coords: { lat: matchedTour?.latitude || 0, lng: matchedTour?.longitude || 0 },
              },
            };
          }
        }
        
        return day;
      });

      return validatedDays;
    });
  }, [selectedTours, isOpen]);

  // Hydrate tourDays with complete tour info when selectedTours changes
  useEffect(() => {
    if (!isOpen || selectedTourDays.length === 0 || selectedTours.length === 0) return;
    
    setSelectedTourDays((prev) => {
      return prev.map((day) => {
        if (day.tour?.title) return day; // Already has title, skip
        
        // Try to find matching tour in selectedTours by tourId or tour.id
        const tourId = String(day.tourId || day.tour?.id || '').trim();
        const matchedTour = selectedTours.find((t: any) => String(t?.id || '').trim() === tourId);
        
        if (!matchedTour) return day;
        
        // Enrich with complete tour data, ensuring tourId is set
        return {
          ...day,
          tourId: String(matchedTour.id),
          tour: {
            ...day.tour,
            ...matchedTour,
          },
        };
      });
    });
  }, [selectedTours, isOpen]);

  // Autosave draft while user is filling data
  React.useEffect(() => {
    if (!isOpen || !draftReady) return;
    try {
      localStorage.setItem(localDraftKey, JSON.stringify({
        passengers,
        totalPassengers,
        selectedTourDays,
        currentCalendarMonth: currentCalendarMonth.toISOString(),
      }));
    } catch {
      // Ignore storage write errors silently
    }
  }, [isOpen, draftReady, localDraftKey, passengers, totalPassengers, selectedTourDays, currentCalendarMonth]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const { isDesktop } = useHeaderOffset();

const totalPrice = useMemo(() => {
    return selectedTourDays.reduce((sum, day) => {
      const fallbackTour = selectedTours.find((t: any) => String(t?.id) === String(day?.tour?.id));
      const price = resolveTourUnitPrice(day.tour) || resolveTourUnitPrice(fallbackTour);
      return sum + (price * totalPassengers);
    }, 0);
  }, [selectedTourDays, selectedTours, totalPassengers]);

  const arePassengersComplete = useMemo(() => {
    return passengers.length > 0 && passengers.every((p) => {
      return Boolean((p.name || '').trim())
        && Boolean((p.documentNumber || '').trim())
        && Boolean((p.nationality || '').trim());
    });
  }, [passengers]);

  /**
   * Validate that each selected tour has exactly one day assigned (1:1 mapping)
   */
  const hasRequiredDays = useMemo(() => {
    if (selectedTourDays.length !== selectedTours.length) {
      return false;
    }

    // Verify each tour appears exactly once
    const assignedTourIds = new Set<string>();
    for (const day of selectedTourDays) {
      const tourId = String(day.tourId || day.tour?.id || '').trim();
      if (!tourId) return false;
      if (assignedTourIds.has(tourId)) {
        // Duplicate tour assigned to multiple days
        return false;
      }
      assignedTourIds.add(tourId);
    }

    // Verify all selected tours are in the assigned set
    for (const tour of selectedTours) {
      const tourId = String(tour?.id || '').trim();
      if (!tourId || !assignedTourIds.has(tourId)) {
        return false;
      }
    }

    return true;
  }, [selectedTourDays, selectedTours]);

  const hasInvalidSelectedDate = selectedTourDays.some((d) => isDisabledDate(d.date));
  const hasMissingDestination = selectedTourDays.some((d) => !d.destination);
  const canProceed = Boolean(selectedPickup)
    && hasRequiredDays
    && !hasInvalidSelectedDate
    && !hasMissingDestination
    && arePassengersComplete;

  const focusMissingSection = useCallback((section: 'pickup' | 'days' | 'passengers') => {
    const refs = {
      pickup: pickupSectionRef,
      days: daysSectionRef,
      passengers: passengersSectionRef,
    };

    setMissingSection(section);
    refs[section].current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => setMissingSection((prev) => (prev === section ? null : prev)), 1800);
  }, []);

  const getFirstMissingSection = useCallback((): 'pickup' | 'days' | 'passengers' | null => {
    if (!selectedPickup) return 'pickup';
    if (!hasRequiredDays || hasInvalidSelectedDate || hasMissingDestination) return 'days';
    if (!arePassengersComplete) return 'passengers';
    return null;
  }, [selectedPickup, hasRequiredDays, hasInvalidSelectedDate, hasMissingDestination, arePassengersComplete]);

  const handleDisabledProceedAttempt = useCallback(() => {
    const missing = getFirstMissingSection();
    if (!missing) return;

    if (missing === 'passengers') {
      validatePassengers();
    }

    focusMissingSection(missing);
  }, [getFirstMissingSection, focusMissingSection]);

  // Add new passenger
  const addPassenger = () => {
    setPassengers(prev => [...prev, {
      id: crypto.randomUUID(),
      name: '',
      documentNumber: '',
      nationality: '',
    }]);
    setTotalPassengers(prev => prev + 1);
  };

  // Remove passenger
  const removePassenger = (index: number) => {
    setPassengers(prev => prev.filter((_, i) => i !== index));
    setTotalPassengers(prev => Math.max(1, prev - 1)); // Ensure at least 1 passenger
    
    // Remove error for this passenger if exists
    if (errors[index]) {
      const newErrors = { ...errors };
      delete newErrors[index];
      setErrors(newErrors);
    }
  };

  // Calendar logic
  const calendarDays = useMemo(() => {
    const start = startOfMonth(currentCalendarMonth);
    const end = endOfMonth(currentCalendarMonth);
    const days = eachDayOfInterval({ start, end });
    
    // Add empty slots for the first week
    const firstDayOfWeek = getDay(start); // 0 (Sun) to 6 (Sat)
    const padding = Array.from({ length: (firstDayOfWeek + 6) % 7 }).map(() => null);
    
    return [...padding, ...days];
  }, [currentCalendarMonth]);

  /**
   * Get next unassigned tour ID.
   * Returns the first tour ID that doesn't have a selected day yet.
   */
  const getNextUnassignedTourId = useCallback((
    currentDays: SelectedTourDay[],
    availableTours: any[]
  ): string | null => {
    const assignedTourIds = new Set(
      currentDays
        .map(d => String(d.tourId || d.tour?.id || '').trim())
        .filter(Boolean)
    );
    
    const unassignedTour = availableTours.find(
      tour => !assignedTourIds.has(String(tour?.id || '').trim())
    );
    
    return unassignedTour ? String(unassignedTour.id) : null;
  }, []);

  /**
   * Build a SelectedTourDay with explicit tour tracking
   */
  const buildTourDay = useCallback((
    dateStr: string,
    tour: any
  ): SelectedTourDay => {
    return {
      date: dateStr,
      tourId: String(tour?.id || ''), // Explicit tracking
      tour: { ...tour },
      destination: {
        displayName: tour?.title || 'Sin nombre',
        coords: { lat: tour?.latitude || 0, lng: tour?.longitude || 0 },
      },
    };
  }, []);

  /**
   * Enhanced day click handler with explicit tour assignment (1 day per tour)
   */
  const handleDayClick = (dateStr: string) => {
    setSelectedTourDays((prev) => {
      // Case 1: Deselecting an already selected date
      const existingIndex = prev.findIndex((d) => d.date === dateStr);
      if (existingIndex >= 0) {
        selectedDayQueueRef.current = selectedDayQueueRef.current.filter((d) => d !== dateStr);
        return prev.filter((_, i) => i !== existingIndex);
      }

      if (selectedTours.length === 0) return prev;

      // Case 2: Adding a new date
      // Check if we have capacity (i.e., can assign a tour to this date)
      const nextUnassignedTourId = getNextUnassignedTourId(prev, selectedTours);
      
      if (nextUnassignedTourId) {
        // Still have unassigned tours: assign the next one to this date
        const tourToAssign = selectedTours.find(
          t => String(t?.id || '').trim() === nextUnassignedTourId
        );
        
        if (!tourToAssign) return prev;
        
        selectedDayQueueRef.current.push(dateStr);

        const newDay = buildTourDay(dateStr, tourToAssign);
        
        return [
          ...prev,
          newDay,
        ].sort((a, b) => a.date.localeCompare(b.date));
      }

      // Case 3: Capacity reached, all tours have a day assigned
      // Replace the oldest selected day (FIFO) with a rotation of tours
      const replaceDate = selectedDayQueueRef.current[0] || prev[0]?.date;
      const toReplace = prev.find((d) => d.date === replaceDate) || prev[0];
      
      if (!toReplace) return prev;
      
      const remaining = prev.filter((d) => d.date !== toReplace.date);
      
      // Get the tour that was on the replaced day and rotate to next in the list
      const replacedTourId = String(toReplace.tourId || toReplace.tour?.id || '').trim();
      const replacedTourIndex = selectedTours.findIndex(
        t => String(t?.id || '').trim() === replacedTourId
      );
      const nextTourIndex = (replacedTourIndex + 1) % selectedTours.length;
      const rotatedTour = selectedTours[nextTourIndex];
      
      selectedDayQueueRef.current = selectedDayQueueRef.current.filter((d) => d !== toReplace.date);
      selectedDayQueueRef.current.push(dateStr);

      const newDay = buildTourDay(dateStr, rotatedTour);

      return [
        ...remaining,
        newDay,
      ].sort((a, b) => a.date.localeCompare(b.date));
    });
  };

  const handleDestinationSelect = (dateStr: string, destination: any) => {
    setSelectedTourDays(prev => prev.map(d => 
      d.date === dateStr ? { ...d, destination } : d
    ));
  };

  const handlePrevMonth = () => setCurrentCalendarMonth(subMonths(currentCalendarMonth, 1));
  const handleNextMonth = () => setCurrentCalendarMonth(addMonths(currentCalendarMonth, 1));

  const handleDragStart = (event: any) => {
    setActiveDragId(event.active.id);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    setActiveDragId(null);
    
    if (active && over && active.id !== over.id) {
      const oldIndex = selectedTourDays.findIndex(d => d.date === active.id);
      const newIndex = selectedTourDays.findIndex(d => d.date === over.id);
      
      if (oldIndex === -1 || newIndex === -1) return;
      
      setSelectedTourDays(prev => {
        // Reorder the entire tourDay objects (preserving all data)
        const reordered = arrayMove([...prev], oldIndex, newIndex);
        return reordered;
      });
    }
  };

  const updatePassenger = (index: number, field: keyof Passenger, value: string) => {
    const newPassengers = [...passengers];
    newPassengers[index] = { ...newPassengers[index], [field]: value };
    setPassengers(newPassengers);
    if (errors[index]) {
      const newErrors = { ...errors };
      delete newErrors[index];
      setErrors(newErrors);
    }
  };

  // Parse YYYY-MM-DD to Date object in local timezone (not UTC)
  const parseDateString = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Get now in tour's timezone
  const getTimeComponentsInTZ = (date: Date, tz: string): { year: number; month: number; day: number; hour: number; minute: number; second: number } => {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    
    const parts = formatter.formatToParts(date);
    const values: Record<string, string> = {};
    parts.forEach(({ type, value }) => {
      values[type] = value;
    });
    
    return {
      year: parseInt(values.year),
      month: parseInt(values.month),
      day: parseInt(values.day),
      hour: parseInt(values.hour),
      minute: parseInt(values.minute),
      second: parseInt(values.second),
    };
  };

  // Format components to "YYYY-MM-DD HH:MM:SS"
  const formatComponents = (c: { year: number; month: number; day: number; hour: number; minute: number; second: number }): string => {
    return `${c.year}-${String(c.month).padStart(2, '0')}-${String(c.day).padStart(2, '0')} ${String(c.hour).padStart(2, '0')}:${String(c.minute).padStart(2, '0')}:${String(c.second).padStart(2, '0')}`;
  };

  // Subtract minutes from time components
  const subtractMinutes = (c: { year: number; month: number; day: number; hour: number; minute: number; second: number }, mins: number): { year: number; month: number; day: number; hour: number; minute: number; second: number } => {
    // Convert to total seconds, subtract, convert back
    const totalSeconds = c.hour * 3600 + c.minute * 60 + c.second - (mins * 60);
    
    let newDay = c.day;
    let newMonth = c.month;
    let newYear = c.year;
    let newSecond = (totalSeconds % 60 + 60) % 60;
    let totalMinutes = Math.floor(totalSeconds / 60);
    let newMinute = (totalMinutes % 60 + 60) % 60;
    let totalHours = Math.floor(totalMinutes / 60);
    let newHour = (totalHours % 24 + 24) % 24;
    
    // If hours went negative, we need to go to previous day
    if (totalHours < 0) {
      newDay -= 1;
      if (newDay < 1) {
        newMonth -= 1;
        if (newMonth < 1) {
          newYear -= 1;
          newMonth = 12;
        }
        const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
        if (newYear % 4 === 0 && (newYear % 100 !== 0 || newYear % 400 === 0)) {
          daysInMonth[1] = 29;
        }
        newDay = daysInMonth[newMonth - 1];
      }
    }
    
    return { year: newYear, month: newMonth, day: newDay, hour: newHour, minute: newMinute, second: newSecond };
  };

  function isDisabledDate(dateString: string): boolean {
    if (!dateString) return false;
    
    try {
      // Get now in tour's timezone
      const timezone = 'America/Argentina/Buenos_Aires';
      const nowComponents = getTimeComponentsInTZ(new Date(), timezone);
      const nowStr = formatComponents(nowComponents);
      
      const dateOnly = dateString; // Format: YYYY-MM-DD
      
      // Extract date from now for comparison
      const nowDateOnly = nowStr.substring(0, 10); // YYYY-MM-DD
      
      // Check if date is in the past
      if (dateString < nowDateOnly) {
        return true;
      }

      // If we have a departure_time, check the 90-minute rule
      // Using the first tour's departure time as reference
      const departure_time = selectedTours[0]?.departure_time;

      if (departure_time) {
        // Parse date components from dateString
        const [depYear, depMonth, depDay] = dateString.split('-').map(Number);
        const [depHour, depMin] = departure_time.split(':').map(Number);
        
        const departureComponents = { year: depYear, month: depMonth, day: depDay, hour: depHour, minute: depMin, second: 0 };
        const latestBookingComponents = subtractMinutes(departureComponents, 90);
        
        const latestBookingStr = formatComponents(latestBookingComponents);
        
        const canBook = nowStr < latestBookingStr;
        return !canBook;
      }

      return false;
    } catch (err) {
      console.error('❌ Error validating date:', err);
      return false;
    }
  }

  const getMinDate = (): string => {
    const today = new Date();
    return format(today, 'yyyy-MM-dd');
  };

  const validatePassengers = (): boolean => {
    const newErrors: Record<number, string> = {};

    passengers.forEach((passenger, index) => {
      const name = (passenger.name || '').trim();
      const doc = (passenger.documentNumber || '').trim();
      const nationality = (passenger.nationality || '').trim();
      
      if (!name) {
        newErrors[index] = 'El nombre es requerido';
      } else if (!doc) {
        newErrors[index] = 'DNI o Pasaporte requerido';
      } else if (!nationality) {
        newErrors[index] = 'La nacionalidad es requerida';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProceed = () => {
    if (!selectedPickup) {
      focusMissingSection('pickup');
      alert('Por favor selecciona un punto de retiro');
      return;
    }

    if (selectedTourDays.length === 0) {
      focusMissingSection('days');
      alert('Por favor selecciona al menos una fecha');
      return;
    }

    if (selectedTourDays.length < selectedTours.length) {
      focusMissingSection('days');
      alert(`Por favor seleccioná ${selectedTours.length} fecha${selectedTours.length > 1 ? 's' : ''} (una por excursión)`);
      return;
    }

    if (selectedTourDays.length > selectedTours.length) {
      focusMissingSection('days');
      alert('Seleccionaste más fechas que excursiones. Cada excursión debe tener exactamente un día.');
      return;
    }

    // Validate 1:1 mapping (each tour assigned to exactly one day)
    const assignedTourIds = new Set<string>();
    for (const day of selectedTourDays) {
      const tourId = String(day.tourId || day.tour?.id || '').trim();
      if (!tourId) {
        focusMissingSection('days');
        alert('Una de las fechas seleccionadas no tiene una excursión asignada');
        return;
      }
      if (assignedTourIds.has(tourId)) {
        focusMissingSection('days');
        alert('Una excursión está asignada a múltiples fechas. Cada excursión debe tener exactamente un día.');
        return;
      }
      assignedTourIds.add(tourId);
    }

    // Verify all tours are assigned
    for (const tour of selectedTours) {
      const tourId = String(tour?.id || '').trim();
      if (!tourId || !assignedTourIds.has(tourId)) {
        focusMissingSection('days');
        alert(`La excursión "${tour?.title}" no tiene una fecha asignada`);
        return;
      }
    }

    if (selectedTourDays.some(d => isDisabledDate(d.date))) {
      focusMissingSection('days');
      alert('Una de las fechas seleccionadas no está disponible');
      return;
    }

    const missingDestinations = selectedTourDays.filter(d => !d.destination);
    if (missingDestinations.length > 0) {
      focusMissingSection('days');
      alert('Por favor selecciona un destino para cada día');
      return;
    }

    if (!validatePassengers()) {
      focusMissingSection('passengers');
      return;
    }

    try {
      localStorage.removeItem(localDraftKey);
    } catch {
      // Ignore storage remove errors silently
    }
    onProceedToCheckout(passengers, selectedTourDays, totalPrice, selectedPickup || undefined);
  };

  if (!isOpen) return null;

  const calendarHelpText = selectedTours.length > 1
    ? 'Selecciona una fecha por cada excursion que agregaste al canasto.'
    : 'Selecciona la fecha para tu excursion.';

  return (
    <div className="flex flex-col h-full min-w-0 overflow-x-hidden bg-background">
      {/* Safe Area Spacer for Mobile */}
      {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}

      {/* Header Title - Fixed */}
      <div className="px-4 py-4 border-b shrink-0 bg-primary text-white">
        <h2 className="font-semibold text-lg text-white">Detalles de Pasajeros</h2>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1 overflow-x-hidden">
        <div className="p-4 space-y-6">
          {/* Punto de Retiro */}
          <div
            ref={pickupSectionRef}
            className={cn(
              'rounded-lg transition-all',
              missingSection === 'pickup' && 'ring-2 ring-destructive/70 bg-destructive/5 p-2 -m-2'
            )}
          >
            <Label className="text-sm font-semibold mb-2 block">
              Punto de Retiro
            </Label>
            {selectedPickup ? (
              <div className="flex items-center justify-between border rounded p-3 min-h-[56px] bg-muted">
                <div className="flex-1">
                  {(selectedPickup.salutation || selectedPickup.firstName || selectedPickup.lastName) && (
                    <div className="text-xs text-muted-foreground mb-1">
                      {[selectedPickup.salutation, selectedPickup.firstName, selectedPickup.lastName].filter(Boolean).join(' ')}
                    </div>
                  )}
                  <div className="font-bold text-sm">{formatAddressForDisplay(selectedPickup.displayName)}</div>
                  {(selectedPickup.floor || selectedPickup.apartment) && (
                    <div className="text-xs text-muted-foreground">
                      {[selectedPickup.floor ? `Piso ${selectedPickup.floor}` : null, selectedPickup.apartment ? `Depto ${selectedPickup.apartment}` : null].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSelectedPickup(null)}
                >
                  Cambiar
                </Button>
              </div>
            ) : addresses.length === 0 ? (
              <div className="space-y-3">
                <div className="bg-amber-50 dark:bg-amber-950/30 border-2 border-amber-300 dark:border-amber-700 rounded-lg p-4">
                  <div className="text-center space-y-2">
                    <div className="text-3xl">📍</div>
                    <h3 className="font-bold text-lg text-amber-900 dark:text-amber-200">¡Elige Tu Dirección!</h3>
                    <p className="text-sm text-amber-800 dark:text-amber-300">Necesitas seleccionar una dirección para continuar</p>
                  </div>
                </div>
                <button
                  onClick={() => setAddressPickerOpen('pickup')}
                  className="w-full flex items-center gap-3 border-2 border-primary rounded-lg p-3 min-h-[56px] bg-primary/5 hover:bg-primary/10 transition-colors text-left font-semibold"
                >
                  <svg className="w-5 h-5 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span className="text-sm">Buscar dirección...</span>
                </button>
                <button
                  type="button"
                  onClick={handleUseCurrentLocationForPickup}
                  className="flex items-center border rounded p-3 min-h-[56px] w-full bg-background hover:bg-muted transition-colors cursor-pointer gap-2"
                >
                  <LocateFixed className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="font-bold text-sm">Tu ubicación actual</span>
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <button
                  onClick={() => setAddressPickerOpen('pickup')}
                  className="w-full flex items-center gap-3 border rounded p-3 min-h-[56px] bg-background hover:bg-muted transition-colors text-left"
                >
                  <svg className="w-5 h-5 text-muted-foreground flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span className="text-sm text-muted-foreground">Buscar dirección...</span>
                </button>
                <button
                  type="button"
                  onClick={handleUseCurrentLocationForPickup}
                  className="flex items-center border rounded p-3 min-h-[56px] w-full bg-background hover:bg-muted transition-colors cursor-pointer gap-2"
                >
                  <LocateFixed className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="font-bold text-sm">Tu ubicación actual</span>
                </button>
                <div className="space-y-2">
                  {addresses.map((addr) => (
                    <button
                      key={addr.id}
                      onClick={() => {
                        setInitialLocationForPicker({ lat: addr.location.latitude, lng: addr.location.longitude });
                        setInitialDisplayNameForPicker(addr.location.displayName);
                        setAddressPickerOpen('pickup');
                      }}
                      className="flex items-center gap-3 border rounded p-3 min-h-[56px] bg-background hover:bg-muted transition-colors cursor-pointer w-full text-left"
                    >
                      <div className="flex-1">
                        <div className="font-bold text-sm">{formatAddressForDisplay(addr.label || addr.location.displayName)}</div>
                        {(addr.floor || addr.apartment) && (
                          <div className="text-xs text-muted-foreground">
                            {[addr.floor ? `Piso ${addr.floor}` : null, addr.apartment ? `Depto ${addr.apartment}` : null].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Fecha - Calendar UI */}
          <div
            ref={daysSectionRef}
            className={cn(
              'space-y-4 border rounded-xl p-4 bg-muted/30 transition-all',
              missingSection === 'days' && 'ring-2 ring-destructive/70 bg-destructive/5 border-destructive/40'
            )}
          >
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-bold flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                Días de Excursiones
              </Label>
              {selectedTourDays.length > 0 && (
                <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-1 rounded-full uppercase tracking-tight">
                  {selectedTourDays.length} {selectedTourDays.length === 1 ? 'día' : 'días'}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground leading-tight">{calendarHelpText}</p>
            
            <div className="bg-background rounded-xl border p-3 shadow-sm">
              {/* Header Mes */}
              <div className="flex items-center justify-between mb-4 px-1">
                <h3 className="text-sm font-bold capitalize">
                  {format(currentCalendarMonth, 'MMMM yyyy', { locale: es })}
                </h3>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handlePrevMonth}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleNextMonth}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Grid de días */}
              <div className="grid grid-cols-7 gap-1 text-center mb-1">
                {['LU', 'MA', 'MI', 'JU', 'VI', 'SA', 'DO'].map(d => (
                  <span key={d} className="text-[10px] font-bold text-muted-foreground/60">{d}</span>
                ))}
              </div>
              
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, i) => {
                  if (!day) return <div key={`empty-${i}`} className="aspect-square" />;
                  
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const today = getTodayDate();
                  const isToday = isSameDay(day, today);
                  const selectedItem = selectedTourDays.find(d => d.date === dateStr);
                  const isSelected = !!selectedItem;
                  const blockedByDate = isDisabledDate(dateStr);
                  const isDisabled = !isSelected && blockedByDate;
                  
                  return (
                    <button
                      key={dateStr}
                      onClick={() => !isDisabled && handleDayClick(dateStr)}
                      disabled={isDisabled}
                      title={isSelected && selectedItem?.tour?.title ? selectedItem.tour.title : ''}
                      className={cn(
                        'aspect-square flex flex-col items-center justify-center rounded-lg text-xs transition-all relative',
                        isSelected && !isDisabledDate(dateStr) ? 'bg-primary text-primary-foreground font-bold shadow-md shadow-primary/20 scale-105 z-10' : 
                        isToday ? 'bg-muted/80 text-primary font-bold border-2 border-primary' : 
                        'hover:bg-muted active:scale-95',
                        !isSelected && blockedByDate && 'opacity-20 cursor-not-allowed grayscale'
                      )}
                    >
                      <span className="font-semibold">{format(day, 'd')}</span>
                      {isToday && !isSelected && <span className="text-[8px] mt-0.5">HOY</span>}
                      {isSelected && !isDisabledDate(dateStr) && (
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-white rounded-full shadow-sm" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Configuración de destinos por día - Reusable Sortable Item */}
            {selectedTourDays.length > 0 && (
              <div className="space-y-4 pt-4 border-t">
                <div className="space-y-1">
                  <Label className="text-base font-bold flex items-center gap-2">
                    <Plus className="w-4 h-4 text-primary" />
                    Itinerario de viaje
                  </Label>
                  <p className="text-xs text-muted-foreground leading-tight">
                    Elige el orden de tus excursiones arrastrándolas para asignar cada una al día indicado.
                  </p>
                </div>
                
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  // onDragStart={handleDragStart} // Not strictly needed for simple sort
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={selectedTourDays.map(d => d.date)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3">
                      {selectedTourDays.map((tourDay) => (
                        <SortableTourItem 
                          key={tourDay.date} 
                          tourDay={tourDay} 
                          onUpdateTour={(date, tour) => {
                            setSelectedTourDays(prev => prev.map(day => 
                              day.date === date ? { 
                                ...day, 
                                tour,
                                destination: {
                                  displayName: tour.title,
                                  coords: { lat: tour.latitude, lng: tour.longitude }
                                }
                              } : day
                            ));
                          }}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            )}

            {/* Info de Validación */}
            <div className="bg-primary/5 rounded-lg p-2.5 flex items-start gap-2 border border-primary/10">
              <Clock className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
              <div className="text-[10px] text-primary/80 leading-relaxed font-medium">
                Podés reservar hasta 90 minutos antes del inicio de la actividad.
              </div>
            </div>
          </div>

          {/* Passengers */}
          <div
            ref={passengersSectionRef}
            className={cn(
              'space-y-4 rounded-lg transition-all',
              missingSection === 'passengers' && 'ring-2 ring-destructive/70 bg-destructive/5 p-2 -m-2'
            )}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Users className="w-4 h-4" />
                Pasajeros ({totalPassengers})
              </h3>
            </div>

            {passengers.map((passenger, index) => (
              <div 
                key={passenger.id} 
                className="border rounded-lg p-4 space-y-3 bg-card hover:bg-accent/5 transition-colors"
              >
                {/* Passenger Number Badge */}
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                      {index + 1}
                    </span>
                    Pasajero {index + 1}
                  </h4>
                  {totalPassengers > 1 && (
                    <button
                      onClick={() => removePassenger(index)}
                      className="text-xs text-destructive hover:text-destructive/80 font-semibold flex items-center gap-1"
                    >
                      <Minus className="w-3 h-3" />
                      Eliminar
                    </button>
                  )}
                </div>

                {/* Name */}
                <div>
                  <Label className="text-xs font-medium">Nombre completo *</Label>
                  <Input
                    placeholder="ej: Juan García"
                    value={passenger.name}
                    onChange={(e) => updatePassenger(index, 'name', e.target.value)}
                    className={cn("text-sm mt-1.5", errors[index] === 'El nombre es requerido' && "border-destructive")}
                  />
                </div>

                {/* ID and Nationality - 2 columns */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium">DNI / Pasaporte *</Label>
                    <Input
                      placeholder="Número"
                      value={passenger.documentNumber}
                      onChange={(e) => updatePassenger(index, 'documentNumber', e.target.value)}
                      className={cn("text-sm mt-1.5", errors[index] === 'DNI o Pasaporte requerido' && "border-destructive")}
                    />
                  </div>

                  <div>
                    <Label className="text-xs font-medium">Nacionalidad</Label>
                    <Input
                      placeholder="ej: Argentina"
                      value={passenger.nationality || ''}
                      onChange={(e) => updatePassenger(index, 'nationality', e.target.value)}
                      className={cn("text-sm mt-1.5", errors[index] === 'La nacionalidad es requerida' && "border-destructive")}
                    />
                  </div>
                </div>

                {/* Error Message */}
                {errors[index] && (
                  <Alert variant="destructive" className="text-xs py-2 border-red-500/50 bg-red-50 dark:bg-red-950/20">
                    <AlertDescription className="flex items-center gap-2">
                      <span className="font-semibold">⚠</span> {errors[index]}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Status indicator */}
                {!errors[index]
                  && passenger.name?.trim()
                  && passenger.documentNumber?.trim()
                  && passenger.nationality?.trim() && (
                  <div className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                    <span>✓</span> Datos completos
                  </div>
                )}

                {/* Add another passenger button - Show after each passenger card */}
                {index === passengers.length - 1 && (
                  <button
                    onClick={addPassenger}
                    className="w-full mt-2 text-xs font-semibold text-primary hover:text-primary/80 flex items-center justify-center gap-2 py-2 rounded-lg border border-primary/20 hover:border-primary/40 hover:bg-primary/5 transition-all"
                  >
                    <Plus className="w-4 h-4" />
                    Agregar otro pasajero
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Resumen */}
          <div className="border-t pt-4 space-y-2 bg-muted/50 -mx-4 px-4 py-4">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Cant. de Días:</span>
              <span className="font-medium">{selectedTourDays.length}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Personas:</span>
              <span className="font-medium">{totalPassengers}</span>
            </div>
            <div className="flex justify-between text-base font-bold pt-2 border-t">
              <span>Total:</span>
              <span className="text-primary font-bold">ARS ${totalPrice.toFixed(0)}</span>
            </div>
          </div>
        </div>
      </ScrollArea>

      {/* Footer */}
      <div className="border-t pt-4 px-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] space-y-2 bg-muted/20 shrink-0">
        <div className="relative">
          <Button
            onClick={handleProceed}
            disabled={loading || !canProceed}
            size="lg"
            className="w-full h-auto py-3 whitespace-normal break-words text-center leading-tight font-bold"
          >
            {loading
              ? 'Procesando...'
              : canProceed
                ? `${proceedLabel} (${selectedTourDays.length} días - $${totalPrice})`
                : proceedLabel}
          </Button>
          {!loading && !canProceed && (
            <button
              type="button"
              aria-label="Completar campos requeridos"
              onClick={handleDisabledProceedAttempt}
              className="absolute inset-0"
            />
          )}
        </div>
        <Button onClick={onClose} variant="outline" size="lg" className="w-full">
          {closeLabel}
        </Button>
      </div>

      {/* Address Picker Sidebar */}
      <AddressPickerSidebar
        ref={addressPickerRef}
        isOpen={addressPickerOpen === 'pickup'}
        addressType={addressPickerOpen}
        onClose={handleAddressPickerClose}
        onSelectAddress={handleSelectAddress}
        userAddresses={addresses}
        sessionUserId={session?.user?.id}
        initialLocation={initialLocationForPicker}
        initialDisplayName={initialDisplayNameForPicker}
      />
    </div>
  );
};
